/**
 * CoverSection — portada A4 con eyebrow + título + cliente + fecha.
 * El branding (logoLetters, name, tagline) viene de `brand` y NO es
 * editable inline (se modifica en /brand).
 */
import type { CSSProperties } from "react";
import type { CoverData, PlanBrand } from "../../../types/plan";
import { absUploadUrl } from "../../../api/client";
import { InlineEditable } from "../InlineEditable";
import { SectionPage } from "./section-shared";

interface CoverSectionProps {
  data: CoverData;
  brand: PlanBrand | null;
  onChange: (patch: Partial<CoverData>) => void;
  readonly?: boolean;
  sectionId?: string;
}

/** Convierte hex (#rrggbb) a rgba con alpha — para los overlays tintados. */
function hexA(hex: string, a: number): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return `rgba(0,0,0,${a})`;
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${a})`;
}

/**
 * Overlay sobre la foto de portada. Siempre incluye un degradado oscuro de base
 * para garantizar contraste con el texto blanco; los modos "color-tint"/"duotone"
 * suman un tinte con los colores de marca (igual que la galería de plantillas).
 */
function coverOverlayStyle(overlay: CoverData["overlay"], primary: string, accent: string): CSSProperties {
  const dark =
    "linear-gradient(180deg, rgba(8,8,8,0.30) 0%, rgba(8,8,8,0.42) 50%, rgba(8,8,8,0.82) 100%)";
  switch (overlay) {
    case "color-tint":
      return { backgroundImage: `linear-gradient(135deg, ${hexA(primary, 0.5)}, ${hexA(accent, 0.38)}), ${dark}` };
    case "duotone":
      return { backgroundImage: `linear-gradient(135deg, ${hexA(primary, 0.58)}, ${hexA(accent, 0.5)}), ${dark}` };
    default:
      return { backgroundImage: dark };
  }
}

export function CoverSection({ data, brand, onChange, readonly, sectionId }: CoverSectionProps) {
  // Split del título en última palabra (visual highlight con color accent)
  const parts = (data.programTitle || "").split(" ");
  const head = parts.slice(0, -1).join(" ");
  const tail = parts.slice(-1)[0] || "";

  const photo = data.imageUrl;
  const primary = brand?.primary || "#7c3aed";
  const accent = brand?.accent || "#ec4899";

  return (
    <SectionPage readonly={readonly} className={"al-cover" + (photo ? " al-cover--photo" : "")}>
      {photo ? (
        <>
          <div
            className="al-cover-photo"
            style={{ backgroundImage: `url(${photo})`, backgroundPosition: data.imagePosition || "center" }}
            aria-hidden="true"
          />
          <div className="al-cover-photo-ov" style={coverOverlayStyle(data.overlay, primary, accent)} aria-hidden="true" />
        </>
      ) : (
        <div className="al-cover-deco" />
      )}

      <header className="al-cover-top">
        <div className="al-cover-brand">
          <div className={"al-cover-brand-mark" + (brand?.logoUrl ? " has-logo" : "")}>
            {brand?.logoUrl ? (
              <img src={absUploadUrl(brand.logoUrl)} alt="" />
            ) : (
              brand?.logoLetters || "AL"
            )}
          </div>
          <div>
            <div className="al-cover-brand-tag">{(brand?.name || "COACH").toUpperCase()}</div>
            <div className="al-cover-brand-name">
              {(brand?.tagline || "ENTRENADOR PERSONAL").toUpperCase()}
            </div>
          </div>
        </div>
        <div className="al-cover-meta">
          <div>PROGRAMA</div>
          <b>
            <InlineEditable
              value={data.month}
              onChange={(v) => onChange({ month: v })}
              readonly={readonly}
              placeholder="Mes"
              sectionId={sectionId}
              fieldPath="month"
            />
          </b>
          <div>
            <InlineEditable
              value={data.duration}
              onChange={(v) => onChange({ duration: v })}
              readonly={readonly}
              placeholder="Duración"
              sectionId={sectionId}
              fieldPath="duration"
            />
          </div>
        </div>
      </header>

      <div className="al-cover-mid">
        <div className="al-cover-eyebrow">
          —{" "}
          <InlineEditable
            value={data.eyebrow}
            onChange={(v) => onChange({ eyebrow: v })}
            readonly={readonly}
            placeholder="Eyebrow"
            sectionId={sectionId}
            fieldPath="eyebrow"
          />
        </div>

        <h1 className="al-cover-title">
          <InlineEditable
            value={head}
            onChange={(v) => onChange({ programTitle: (v + " " + tail).trim() })}
            readonly={readonly}
            placeholder="Título"
            sectionId={sectionId}
            fieldPath="programTitle.head"
          />{" "}
          <span className="al-cover-title-accent">
            <InlineEditable
              value={tail}
              onChange={(v) => onChange({ programTitle: (head + " " + v).trim() })}
              readonly={readonly}
              placeholder=""
              sectionId={sectionId}
              fieldPath="programTitle.tail"
            />
          </span>
        </h1>

        <p className="al-cover-sub">
          <InlineEditable
            value={data.programSubtitle}
            onChange={(v) => onChange({ programSubtitle: v })}
            multiline
            readonly={readonly}
            placeholder="Subtítulo / descripción"
            sectionId={sectionId}
            fieldPath="programSubtitle"
          />
        </p>
      </div>

      <footer className="al-cover-bot">
        <div>
          <div className="al-cover-bot-lbl">CLIENTE</div>
          <b>
            <InlineEditable
              value={data.clientName}
              onChange={(v) => onChange({ clientName: v })}
              readonly={readonly}
              placeholder="Nombre del cliente"
              sectionId={sectionId}
              fieldPath="clientName"
            />
          </b>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="al-cover-bot-lbl">CONTACTO</div>
          <b>{brand?.web || ""}</b>
          <div>{brand?.ig || ""}</div>
        </div>
      </footer>
    </SectionPage>
  );
}
